import { Cesium } from 'app/cesium';
import { ConfigService } from 'app/config/config.service';
import { AsyncPrimitive } from 'app/map/geometry/cesium/AsyncPrimitive/AsyncUpdatablePrimitive';
import {
    createImageOverlayAsyncPrimitive,
    createPolygonAsyncPrimitive,
    createPolylineAsyncPrimitive,
    ImageOverlayAsyncPrimitiveAttributes,
    PolygonAsyncPrimitiveAttributes,
    PolylineAsyncPrimitiveAttributes,
} from 'app/map/geometry/cesium/AsyncPrimitive/utils';
import { SelectionType } from 'app/shared/selection/SelectionType';
import { LabelCollection } from 'cesium';

function defaultLabel(): Cesium.Label {
    const l = {} as Cesium.Label;
    l.font = `600 ${ConfigService.cesiumZoneLabelPixelSize}px Helvetica`;
    l.scale = 1;
    l.fillColor = Cesium.Color.WHITE;
    l.style = Cesium.LabelStyle.FILL_AND_OUTLINE;
    // TODO constify these scalars/cartesian2?
    l.translucencyByDistance = new Cesium.NearFarScalar(1.5e3, 1.0, 1.5e6, 0.0);
    l.horizontalOrigin = Cesium.HorizontalOrigin.CENTER;
    return l;
}

function copyLabel(currentLabel: Cesium.Label): Cesium.Label {
    const l = defaultLabel();
    const cl = currentLabel;

    l.text = cl.text;
    l.show = cl.show;
    l.eyeOffset = cl.eyeOffset;
    l.position = cl.position;
    return l;
}

/**
 * CesiumZone is a convenience wrapper for the primitives in cesium that make up a zone.
 * The pattern used across the board is changing things on the CesiumZone 'wrapper', and diffing them
 * to work out if a zone re-creation is needed, and then applying them every tick (which at the moment is every 1s)
 */
export class CesiumZone {
    private readonly _labelCollection: LabelCollection;

    private readonly _id: number;
    private readonly _allowSelection: boolean;

    // State
    private _isSelected: boolean;
    private _showPolygon: boolean;
    private _showLabel: boolean;
    private _editingPoints: boolean;
    private _positions: Array<Cesium.Cartesian3>;
    private _color: Cesium.Color;
    private _outlineColor: Cesium.Color;
    private _outlineWidth: number;
    private _zIndex: number;
    private _alpha: number;
    private _overlayImage: string;

    // This is used to determine whether or not the polygon needs to be updated on the next tick
    private _polygonRefreshRequired: boolean;
    // This is used to determine whether or not the label needs to be update on the next tick
    private _labelRefreshRequired: boolean;

    public label: Cesium.Label;
    private readonly asyncPrimitive: AsyncPrimitive<PolygonAsyncPrimitiveAttributes>;
    private readonly asyncOutlinePrimitive: AsyncPrimitive<PolylineAsyncPrimitiveAttributes>;
    private readonly asyncImageOverlayPrimitive: AsyncPrimitive<ImageOverlayAsyncPrimitiveAttributes>;

    constructor(
        id: number,
        labelCollection: Cesium.LabelCollection,
        polygonPrimitiveCollection: Cesium.PrimitiveCollection,
        allowSelection: boolean = true,
        selectionType: SelectionType = SelectionType.ZONE,
    ) {
        this._id = id;
        this._allowSelection = allowSelection;

        this._labelCollection = labelCollection;
        this.asyncPrimitive = createPolygonAsyncPrimitive(
            this.id,
            polygonPrimitiveCollection,
            allowSelection,
            selectionType,
        );
        this.asyncOutlinePrimitive = createPolylineAsyncPrimitive(
            this.id,
            polygonPrimitiveCollection,
            allowSelection,
        );
        this.asyncImageOverlayPrimitive = createImageOverlayAsyncPrimitive(
            this.id,
            polygonPrimitiveCollection,
        );

        // Polygon details
        this._positions = [];
        this._color = Cesium.Color.WHITE;

        this.label = defaultLabel();
        this._labelRefreshRequired = false;
        this._createLabel();
    }

    public destroy(): void {
        this.asyncPrimitive.destroy();
        this.asyncOutlinePrimitive?.destroy();
        this.asyncImageOverlayPrimitive?.destroy();
        this._labelCollection.remove(this.label);
        this.label = null;
    }

    public get id(): number {
        return this._id;
    }

    public set labelText(text: string) {
        this.label.text = text;
    }

    public get labelText(): string {
        return this.label.text;
    }

    public set labelPosition(position: Cesium.Cartesian3) {
        // if you pass in a nil position - we'll ignore it because you can't do that
        //  hopefully you're about to hide the label - otherwise it's just going to stick around!
        if (position) {
            // the first thing a Cesium Entity will do is diff the previous value for us
            //  so no need to do that first here
            this.label.position = position;
        }
    }

    public get labelPosition(): Cesium.Cartesian3 {
        return this.label.position;
    }

    public get positions(): Array<Cesium.Cartesian3> {
        return this._positions;
    }

    public set positions(positions: Array<Cesium.Cartesian3>) {
        if (!CesiumZone.isPositionsNotEqual(positions, this._positions)) {
            return;
        }
        // make sure we take a copy just incase
        this._positions = [...positions];
        this._polygonRefreshRequired = true;
    }

    // Set the label to appear through the terrain
    public setLabelCannotBeOccluded(): void {
        this.label.disableDepthTestDistance = Number.POSITIVE_INFINITY;
    }

    // set the label height to the height of the position on the scene globe
    public clampLabelToGround(): void {
        this.label.heightReference = Cesium.HeightReference.CLAMP_TO_GROUND;
    }

    /**
     * Similar to the above setter for positions, but returns whether or not the positions actually changed
     * @param positions
     */
    public changePositionsIfNeeded(positions: Array<Cesium.Cartesian3>): boolean {
        if (!CesiumZone.isPositionsNotEqual(positions, this._positions)) {
            return false;
        }
        // make sure we take a copy just incase
        this._positions = [...positions];
        this._polygonRefreshRequired = true;
        return true;
    }

    public set alpha(value: number) {
        this._alpha = value;
    }

    public set outlineWidth(value: number) {
        this._outlineWidth = value;
    }

    public set zIndex(value: number) {
        this._zIndex = value;
    }

    public set overlayImage(value: string) {
        this._overlayImage = value;
    }

    public set outlineColor(outlineColor: Cesium.Color) {
        if (cesiumIsEqual(outlineColor, this._outlineColor)) {
            return;
        }
        this._outlineColor = outlineColor;
        this._polygonRefreshRequired = true;
    }

    public get color(): Cesium.Color {
        return this._color;
    }

    public set color(color: Cesium.Color) {
        if (cesiumIsEqual(color, this._color)) {
            return;
        }
        this._color = color;
        this._polygonRefreshRequired = true;
    }

    public set showLabel(show: boolean) {
        if (show === this._showLabel) {
            return;
        }

        this._showLabel = show;
        this._labelRefreshRequired = true;
    }

    public get showPolygon() {
        return this._showPolygon;
    }

    public set showPolygon(show: boolean) {
        if (show === this._showPolygon) {
            return;
        }

        this._showPolygon = show;
        this._polygonRefreshRequired = true;
    }

    public select(): void {
        if (this._isSelected) {
            return;
        }

        this._isSelected = true;
        this._polygonRefreshRequired = true;
    }

    public deselect(): void {
        if (!this._isSelected) {
            return;
        }

        this._isSelected = false;
        this._polygonRefreshRequired = true;
    }

    private _removeLabel(): void {
        this.label.show = false;
        const newLabel = copyLabel(this.label);
        this._labelCollection.remove(this.label);
        this.label = newLabel;
    }

    private _createLabel(): void {
        this.label.show = true;
        this.label = this._labelCollection.add(this.label);
    }

    public set eyeOffset(eyeOffset: Cesium.Cartesian3) {
        if (!this.label) {
            return;
        }
        this.label.eyeOffset = eyeOffset;
    }

    private getColorWithAlpha(): Cesium.Color {
        const alpha = this._alpha ?? this.getSelectedAlpha();
        const color = this._color || Cesium.Color.WHITE;
        return color.withAlpha(alpha);
    }

    private _updatePolygon(): void {
        const color = this.getColorWithAlpha();
        this.asyncPrimitive
            .setAttributes({
                color,
                positions: this.positions,
                show: this._showPolygon,
                zIndex: this._zIndex ?? 0,
            })
            .catch((e) => console.error(e));
        if (this._outlineWidth && this._outlineColor) {
            this.asyncOutlinePrimitive
                .setAttributes({
                    color: this._outlineColor,
                    positions: this.positions,
                    show: this._showPolygon,
                    width: this._outlineWidth,
                    zIndex: this._zIndex ?? 0,
                })
                .catch((e) => console.error(e));
        }
        if (this._overlayImage) {
            this.asyncImageOverlayPrimitive
                .setAttributes({
                    positions: this.positions,
                    show: this._showPolygon,
                    labelPosition: this.labelPosition,
                    image: this._overlayImage,
                    color: Cesium.Color.WHITE.withAlpha(this._outlineColor.alpha),
                })
                .catch((e) => console.error(e));
        }
    }

    public set editingPoints(editingPoints: boolean) {
        if (editingPoints === this._editingPoints) {
            return;
        }
        this._editingPoints = editingPoints;
        this._polygonRefreshRequired = true;
    }

    public hasPositions(): boolean {
        return this.positions && this.positions.length > 0;
    }

    private getSelectedAlpha(): number {
        if (this._editingPoints) {
            return 0.2;
        } else {
            return this._isSelected ? 0.55 : 0.35;
        }
    }

    public set selected(selected: boolean) {
        if (selected === this._isSelected) {
            return;
        }
        this._isSelected = selected;
        this._polygonRefreshRequired = true;
    }

    public get selected(): boolean {
        return this._isSelected;
    }

    /**
     * Applies pending changes to a zone.
     * This pattern is used for performance reasons, it ensures that zones are not
     * destroyed and re-created any more often than they need to be
     */
    public applyPendingRefreshes(): void {
        if (this._labelRefreshRequired) {
            this._removeLabel();
            if (this._showLabel) {
                this._createLabel();
            }
            this._labelRefreshRequired = false;
        }

        if (this._polygonRefreshRequired) {
            this._updatePolygon();
            this._polygonRefreshRequired = false;
        }
    }

    /**
     * Returns true if the two supplied Cartesian3 arrays are different, otherwise returns false
     * @param oldPositions
     * @param newPositions
     */
    public static isPositionsNotEqual(
        oldPositions: Cesium.Cartesian3[],
        newPositions: Cesium.Cartesian3[],
    ): boolean {
        // If lengths are different, they're not the same.
        if (oldPositions.length != newPositions.length) {
            return true;
        }
        // Find an unequal position, order matters.
        return !!oldPositions.find((oldPosition: Cesium.Cartesian3, i: number): boolean => {
            const newPosition = newPositions[i];
            const different = !newPosition.equals(oldPosition);
            if (different) {
                // Compare floating point accuracy
                // We do this because positions seem to be slightly different when applied as a primitive vs. pulled from collection.

                // 6 Decimal Point Accuracy.
                const FLOAT_ACCURACY = 0.0000001;
                return (
                    Math.abs(oldPosition.x - newPosition.x) > FLOAT_ACCURACY ||
                    Math.abs(oldPosition.y - newPosition.y) > FLOAT_ACCURACY ||
                    Math.abs(oldPosition.z - newPosition.z) > FLOAT_ACCURACY
                );
            }
            return false;
        });
    }
}

// Function to help check equality for cesium things
function cesiumIsEqual(a, b) {
    if (!a && !b) {
        return true;
    }

    if (a && b && a.equals(b)) {
        return true;
    }
    return false;
}
